import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

interface Order {
  id: string
  status: string
  total: number
  createdAt: string
}

interface RecentOrdersProps {
  orders: Order[] | undefined
  isLoading?: boolean
  'data-testid'?: string
}

export default function RecentOrders({
  orders,
  isLoading = false,
  'data-testid': testId = 'recent-orders',
}: RecentOrdersProps) {
  const { t } = useTranslation('admin')
  return (
    <Card 
      data-testid={testId}
      style={{
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--border)',
        boxShadow: 'var(--shadow-card)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <CardHeader>
        <CardTitle
          style={{
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-heading)',
            fontSize: 'var(--text-xl)',
          }}
        >
          {t('dashboard.recentOrders')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : !orders || orders.length === 0 ? (
          <p 
            className="text-sm"
            style={{
              color: 'var(--text-secondary)',
            }}
            data-testid={`${testId}-empty`}
          >
            {t('dashboard.noRecentOrders')}
          </p>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between pb-3 last:pb-0"
                style={{
                  borderBottom: '1px solid var(--border)',
                }}
                data-testid={`${testId}-item-${order.id}`}
              >
                <div className="flex-1">
                  <div 
                    className="font-medium"
                    style={{
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-body)',
                    }}
                    data-testid={`${testId}-item-${order.id}-id`}
                  >
                    {t('dashboard.orderNumber', { id: order.id.slice(0, 8) })}
                  </div>
                  <div 
                    className="text-sm"
                    style={{
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {format(new Date(order.createdAt), 'MMM d, yyyy HH:mm')}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="font-medium"
                    style={{
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-body)',
                    }}
                    data-testid={`${testId}-item-${order.id}-total`}
                  >
                    ${order.total.toFixed(1)}
                  </div>
                  <div
                    className="text-sm capitalize"
                    style={{
                      color: 'var(--text-secondary)',
                    }}
                    data-testid={`${testId}-item-${order.id}-status`}
                  >
                    {order.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

